import * as React from 'react';
import _ from 'lodash';
import bind from 'bind-decorator';
import type TurbolinksModule from 'turbolinks';
import type { Location } from 'turbolinks/dist/location';
import styled from 'styled-components';

import type { ReduxState } from '@/redux/app/state';
import * as actions from '@/redux/app/actions/navigation';
import { connect } from '@/lib/redux/redux-connect';
import { navigationHistorySelector } from '@/redux/app/selectors/navigation';
import { pathToLocation } from '@/lib/url';

import type { Pane } from './Pane';
import { AppNavigatorDelegateContext, AppNavigatorParamsContext, AppNavigatorPathContext } from './app-navigator.context';
import { pathToList } from './utils';

export const parsePath = (path: string) => (
  path ? { asString: path, asList: pathToList(path) } : null
);

const NavigationContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;

  .pane-container {
    background: white;
    position: relative;
  }

  .pane-container:last-child {
    flex: 1;
    overflow: auto;
  }
`;

declare global {
  interface Window {
    Turbolinks: typeof TurbolinksModule & {
      Location: typeof Location,
      uuid(): string,
    }
  }
}

const { Turbolinks } = window;
const { visit, historyPoppedToLocationWithRestorationIdentifier } = Turbolinks.controller;

const mapStateToProps = (state: ReduxState) => ({
  history: navigationHistorySelector(state),
});

type NavigateParams = Parameters<typeof actions['navigate']>;
type OpenParams = Parameters<typeof actions['open']>;
type BackParams = Parameters<typeof actions['back']>;

export interface AppNavigatorProps {
  defaultPath: string;
  defaultParams: Record<string, string>;
}

export interface AppNavigatorState {
  currentPath: {
    asString: string;
    asList: string[];
  };
  currentParams: Record<string, string>;
}

const Connected = connect(mapStateToProps, actions);

@Connected
export class AppNavigator extends Connected.Component<AppNavigatorProps>() {
  static mountedInstance: AppNavigator = null;

  PrimaryPaneContent: React.ComponentType = null;

  primaryPane: Pane = null;

  erroredPane: Pane = null;

  state = {
    currentPath: null,
    currentParams: null,
  };

  static getDerivedStateFromProps(
    nextProps: ReturnType<typeof mapStateToProps>, prevState: AppNavigatorState,
  ) {
    const { path, params } = _.last(nextProps.history) || {};
    let newState = null;
    if (path !== prevState.currentPath?.asString) {
      newState = { ...newState, currentPath: parsePath(path) };
    }
    if (params !== prevState.currentParams) {
      newState = { ...newState, currentParams: params };
    }
    return newState;
  }

  componentDidMount() {
    if (AppNavigator.mountedInstance) console.warn('AppNavigator should only be rendered once.');
    AppNavigator.mountedInstance = this;

    const { defaultPath, defaultParams } = this.props;

    Object.assign(Turbolinks.controller, {
      visit(location, options) {
        const url = new URL(location.toString());
        if (url.searchParams.get('react') !== 'false') {
          AppNavigator.mountedInstance.gotoUrl(url, 'visit');
        } else {
          visit.call(this, location, options);
        }
      },
      historyPoppedToLocationWithRestorationIdentifier(location, restorationIdentifier) {
        const url = new URL(location.toString());
        if (url.searchParams.get('react') !== 'false') {
          this.restorationIdentifier = restorationIdentifier;
          this.location = Turbolinks.Location.wrap(location);
          AppNavigator.mountedInstance.gotoUrl(url, 'history');
        } else {
          historyPoppedToLocationWithRestorationIdentifier
            .call(this, location, restorationIdentifier);
        }
      },
    } as typeof Turbolinks.controller);

    if (Turbolinks.controller.currentVisit?.action === 'restore') {
      // Navigated away from React and now going back
      const Content = this.primaryPane?.getContent();
      if (typeof Content === 'undefined') this.dispatchBack(null, 'history');
    } else {
      this.dispatchNavigate({ path: defaultPath, params: defaultParams }, 'history');
    }
  }

  componentWillUnmount() {
    AppNavigator.mountedInstance = null;
    Object.assign(
      Turbolinks.controller,
      { visit, historyPoppedToLocationWithRestorationIdentifier },
    );
  }

  componentDidUpdate() {
    if (!this.primaryPane) return;
    const Content = this.primaryPane.getContent();
    if (Content === this.PrimaryPaneContent) return;
    this.PrimaryPaneContent = Content;

    if (typeof Content !== 'undefined') return;
    const { history } = this.redux;
    if (history.length < 1) return;

    const { path, params } = _.last(history);
    Object.assign(
      Turbolinks.controller,
      { visit, historyPoppedToLocationWithRestorationIdentifier },
    );
    Turbolinks.visit(pathToLocation(path, params), { action: 'replace' });
  }

  registerPrimaryPane(pane: Pane) {
    this.primaryPane = pane;
  }

  deregisterPrimaryPane(pane: Pane) {
    if (this.primaryPane === pane) this.primaryPane = null;
  }

  registerError(pane: Pane, error: Error) {
    if (this.erroredPane) throw error; // If more than one pane errored, propagate further up
    this.erroredPane = pane;
  }

  deregisterError(pane: Pane) {
    if (pane === this.erroredPane) this.erroredPane = null;
  }

  gotoUrl(url: URL, origin: 'visit'|'history' = 'visit') {
    const { history } = this.redux;
    url.searchParams.delete('react');
    const route = { path: url.pathname, params: Object.fromEntries(url.searchParams.entries()) };
    if (history.some(({ path }) => path === url.pathname)) this.dispatchBack(route, origin);
    else this.dispatchNavigate(route, origin);
  }

  getCurrentPath() {
    return this.state.currentPath;
  }

  getCurrentParams() {
    return this.state.currentParams;
  }

  @bind
  dispatchNavigate(route: NavigateParams[0], origin?: NavigateParams[1]) {
    const { navigate } = this.redux;
    const routeUpdater = this.deferRouteUpdate(route);
    navigate(route, origin, routeUpdater);
    routeUpdater.update();
  }

  @bind
  dispatchOpen(route: OpenParams[0]) {
    const { open } = this.redux;
    return open(route);
  }

  @bind
  dispatchBack(route: BackParams[0], origin?: BackParams[1]) {
    const { back } = this.redux;
    return back(route, origin);
  }

  protected deferRouteUpdate(newRoute: NavigateParams[0]) {
    let resolvePromise: (value: AppNavigatorState) => void;
    const promise = Object.assign(
      new Promise<AppNavigatorState>((resolve) => {
        resolvePromise = resolve;
      }),
      {
        update: () => {
          this.setState({
            currentPath: parsePath(newRoute.path),
            currentParams: newRoute.params,
          }, () => resolvePromise(this.state));
        },
      },
    );

    return promise;
  }

  render() {
    const { children } = this.props;
    return (
      <AppNavigatorDelegateContext.Provider value={this}>
        <NavigationContainer id="app-navigation-container">
          <AppNavigatorPathContext.Provider value={this.getCurrentPath()}>
            <AppNavigatorParamsContext.Provider value={this.getCurrentParams()}>
              {children}
            </AppNavigatorParamsContext.Provider>
          </AppNavigatorPathContext.Provider>
        </NavigationContainer>
      </AppNavigatorDelegateContext.Provider>
    );
  }
}
